const DEFAULT_APP_FUNCTOR_PORT = 5222;
const DEFAULT_APP_WORKER_PORT = 5410;
const DEFAULT_NLS_SERVICE_PORT = 3391;

function trimBaseUrl(value: string | undefined, defaultPort: number): string {
  const explicit = value?.trim();
  if (explicit) {
    return explicit.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:${defaultPort}`;
    }
  }
  return `http://localhost:${defaultPort}`;
}

const appFunctorBaseUrl = trimBaseUrl(
  import.meta.env.VITE_APP_FUNCTOR_BASE_URL || import.meta.env.VITE_API_BASE_URL,
  DEFAULT_APP_FUNCTOR_PORT,
);

const workspaceExplicit = (
  import.meta.env.VITE_APP_FUNCTOR_WORKSPACE_BASE_URL
  || import.meta.env.VITE_KPWORKSPACE_BASE_URL
)?.trim();

const appFunctorWorkspaceBaseUrl = workspaceExplicit
  ? workspaceExplicit.replace(/\/+$/, '')
  : appFunctorBaseUrl;

const appWorkerBaseUrl = trimBaseUrl(
  import.meta.env.VITE_APP_WORKER_BASE_URL || import.meta.env.VITE_KPREACTOR_BASE_URL,
  DEFAULT_APP_WORKER_PORT,
);

/** OpenAPI Skills Gateway — custom domain only (never pack fcapp.run into VITE_). */
const DEFAULT_OPENAPI_GATEWAY_PORT = 9100;
const openapiGatewayBaseUrl = trimBaseUrl(
  import.meta.env.VITE_OPENAPI_GATEWAY_BASE_URL,
  DEFAULT_OPENAPI_GATEWAY_PORT,
);

const nlsServiceBaseUrl = trimBaseUrl(
  import.meta.env.VITE_NLS_SERVICE_BASE_URL,
  DEFAULT_NLS_SERVICE_PORT,
);

/** AppFactory — batch particle factory (not FC; never pack fcapp.run). */
const appFactoryBaseUrl = (() => {
  const explicit = import.meta.env.VITE_APP_FACTORY_BASE_URL?.trim();
  if (explicit) return explicit.replace(/\/+$/, '');
  // Default to deployed ECS. Local factory is opt-in:
  // VITE_APP_FACTORY_BASE_URL=http://127.0.0.1:5610
  // (Avoid plain `npm run dev` silently hitting a stale localhost:5610 without new routes.)
  return 'https://factory.kelilearning.com';
})();

export function isCloudBackendMode(): boolean {
  if (import.meta.env.VITE_WEBAPP2_DEV_MODE === 'remote') {
    return true;
  }
  try {
    const parsed = new URL(appFunctorBaseUrl);
    return parsed.protocol === 'https:' || !['localhost', '127.0.0.1'].includes(parsed.hostname);
  } catch {
    return false;
  }
}

export function buildAppFunctorNetworkErrorMessage(path: string): string {
  const shortPath = path.split('?')[0].slice(0, 60);
  const target = `${appFunctorBaseUrl}${shortPath}`;
  if (isCloudBackendMode()) {
    return `无法连接云端 AppFunctor ${target}。请确认 appfunctor.keliriji.com 已部署且服务正常。`;
  }
  return `无法连接 AppFunctor ${target}，请确认本地 AppFunctor (npm run dev) 已启动。`;
}

export function buildAppFunctorWorkspaceNetworkErrorMessage(pathname: string): string {
  const shortPath = pathname.split('?')[0].slice(0, 60);
  const target = `${appFunctorWorkspaceBaseUrl}${shortPath}`;
  if (isCloudBackendMode()) {
    return `无法连接云端 Workspace ${target}。`;
  }
  return `无法连接 Workspace ${target}，请确认 AppFunctor 已启动。`;
}

export const appConfig = {
  appName: '颗粒日记',
  sitePublicUrl: (import.meta.env.VITE_SITE_PUBLIC_URL || 'https://www.keliriji.com').replace(/\/+$/, ''),
  /** 微信网站应用授权从 kelilearning 发起，审核回调域挂公司主体 */
  wechatOAuthStartUrl: (import.meta.env.VITE_WECHAT_OAUTH_START_URL || 'https://www.kelilearning.com').replace(/\/+$/, ''),
  privacyPolicyUrl: import.meta.env.VITE_PRIVACY_POLICY_URL || 'https://www.keliriji.com/privacy',
  termsUrl: import.meta.env.VITE_TERMS_URL || 'https://www.keliriji.com/terms',
  copyrightUrl: import.meta.env.VITE_COPYRIGHT_URL || 'https://www.keliriji.com/copyright',
  supportUrl: import.meta.env.VITE_SUPPORT_URL || 'https://www.keliriji.com/support',
  icpWebsite: '京ICP备2026007820号-1',
  appFunctorBaseUrl,
  appFunctorWorkspaceBaseUrl,
  appWorkerBaseUrl,
  /** Knowledge-particle factory API (factory.kelilearning.com). */
  appFactoryBaseUrl,
  /** Developer API-Key management + Agent Open API edge (scheme B). */
  openapiGatewayBaseUrl,
  apiBaseUrl: appFunctorBaseUrl,
  kpWorkspaceBaseUrl: appFunctorWorkspaceBaseUrl,
  kpReactorBaseUrl: appWorkerBaseUrl,
  nlsServiceBaseUrl,
  /**
   * keliagent public API (oral shard extract). Custom domain only — never fcapp.run.
   * Auth: AppFunctor access JWT on Authorization header.
   */
  keliagentBaseUrl: (
    import.meta.env.VITE_KELIAGENT_BASE_URL || 'https://agent.kelilearning.com'
  ).replace(/\/+$/, ''),
  accessHeaderName: 'Authorization',
  refreshTransport: 'header' as const,
  defaultTimeoutMs: 20000,
  /** 素材品控 re-run：视觉模型可能较慢 */
  qualityControlRunTimeoutMs: 180000,
  /** 品控触发的重新提取提交超时 */
  particleExtractionSubmitTimeoutMs: 120000,
  /**
   * Workbench assess（含 1anq /assess）：不得短于远程 FC 超时（5 分钟）。
   * 略高于 5 分钟，避免 FC 刚跑满就被前端 Abort。
   */
  ttqWorkbenchAssessTimeoutMs: 360000,
  /**
   * Workbench run（含 1anq /generate · 重新生成）：默认 10 分钟，
   * 必须 ≥ 远程 FC 超时（5 分钟）。
   */
  ttqWorkbenchRunTimeoutMs: 600000,
  /** 隐性能力 PAP 提取轮询窗口（与 Mobile2 papExtractTimeoutMs 对齐） */
  papExtractTimeoutMs: 600000,
  liveUpdatesMode: (import.meta.env.VITE_LIVE_UPDATES_MODE || 'poll') as 'poll' | 'sse',
  buildKpWorkspaceParticleUrl(
    particleId: string,
    particleTitle?: string,
    options: { workspaceSessionId?: string; source?: string } = {},
  ): string {
    const params = new URLSearchParams({ source: options.source?.trim() || 'webapp2' });
    if (particleTitle?.trim()) params.set('particleTitle', particleTitle.trim());
    if (options.workspaceSessionId?.trim()) params.set('workspaceSessionId', options.workspaceSessionId.trim());
    return `${appFunctorWorkspaceBaseUrl}/workspace/knowledge-particles/${encodeURIComponent(particleId)}?${params}`;
  },
  buildKpWorkspaceAssetUrl(assetId: string, options: { source?: string } = {}): string {
    const params = new URLSearchParams({ source: options.source?.trim() || 'webapp2' });
    return `${appFunctorWorkspaceBaseUrl}/workspace/assets/${encodeURIComponent(assetId)}?${params}`;
  },
};
